/* =========================================================
   NVEDA — CART-LEVEL FREE GIFT
   Works in cart drawer, so it carries into BOTH
   GoKwik checkout and Shopify native checkout.

   Place in: assets/nveda-free-gift.js
   Load in theme.liquid before </body>:
   {{ 'nveda-free-gift.js' | asset_url | script_tag }}

   PATCH (v11-fix2): fixes duplicate gift line-items caused by a race
   between concurrent add triggers. See addGift()/dedupeGiftLines()
   below for details. Root-caused against order #NVEDA340685, which
   shipped with two separate qty-1 gift lines instead of one.

   PATCH (v11-fix3): deliberately NOT touching checkout. The fix is
   entirely at the cart level: addGift() is now mutexed (addLock) so
   concurrent triggers share one in-flight add instead of racing
   separate ones, and dedupeGiftLines() collapses any stray duplicate
   down to exactly one line, qty 1. It runs inside sync() — which
   fires on every cart add/change, every drawer open, and every 2.5s
   while the drawer is open — so even a rare race gets self-corrected
   within a couple of seconds, well before checkout starts. When it
   does catch a duplicate it logs it (console + dataLayer event +
   'nveda:gift-anomaly' DOM event) and shows a brief on-page banner,
   so it's never silent.

   NOTE: this is a client-side guarantee at the cart level, which is
   the level you control. It doesn't reach into GoKwik's own hosted
   checkout (a separate domain this script has no visibility into) —
   but by the time a real customer gets there, the cart has already
   been deduped multiple times over. If you ever want a true
   backend-side backstop (e.g. a Shopify Flow that flags any order
   with >1 line of SKU 8906100500124_1), that's a separate, optional
   piece of work — ask if you want it.
   ========================================================= */

(function () {
  'use strict';

  // Proof-of-execution marker. Console check: window.__NVEDA_GIFT__
  window.__NVEDA_GIFT__ = 'v11-fix3';

  const PROMPT_ID = 'nveda-gift-prompt';
  const BANNER_ID = 'nveda-gift-banner';
  const ANOMALY_EVENT = 'nveda:gift-anomaly';

  // Fires whenever the code catches a state it shouldn't be in (a
  // duplicate gift line, a quantity >1, a checkout-guard failure).
  // Never throws — this is observability, not control flow. Pushes to
  // dataLayer (GA4/GTM, already on the store) and a DOM CustomEvent so
  // anything else — an app, a Slack webhook script, etc. — can listen
  // without touching this file.
  function logGiftAnomaly(reason, detail) {
    try {
      console.warn('[nveda-gift] anomaly:', reason, detail || '');
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'nveda_gift_anomaly',
        nveda_gift_reason: reason,
        nveda_gift_detail: detail || null
      });
      document.dispatchEvent(new CustomEvent(ANOMALY_EVENT, {
        detail: { reason, detail: detail || null, at: Date.now() }
      }));
    } catch (_) {
      // Logging must never be the thing that breaks the cart.
    }
  }

  // Visible, auto-dismissing notice. Used only for anomalies the
  // customer should plausibly know about (we quietly fixed an extra
  // gift line) — not for routine "gift already here" checks.
  function showBanner(message) {
    try {
      const host =
        document.querySelector('.m-cart-drawer__items') ||
        document.querySelector('[data-minimog-cart-items]') ||
        document.body;
      if (!host) return;

      let box = document.getElementById(BANNER_ID);
      if (!box) {
        box = document.createElement('div');
        box.id = BANNER_ID;
        box.className = 'nveda-gift-banner';
        host.prepend(box);
      }
      box.textContent = message;
      box.style.display = 'block';
      clearTimeout(box.__hideTimer);
      box.__hideTimer = setTimeout(() => { box.style.display = 'none'; }, 5000);
    } catch (_) {}
  }
  const PROMPT_CSS = `
    #${'nveda-gift-prompt'} {
      display: flex; align-items: center; justify-content: space-between;
      gap: 12px; margin: 0 0 14px; padding: 12px 14px;
      border: 1px solid rgba(0,0,0,.12); border-radius: 6px;
      font-size: 14px; line-height: 1.35;
    }
    #${'nveda-gift-prompt'} button {
      flex: 0 0 auto; cursor: pointer; border: 0; border-radius: 4px;
      padding: 8px 14px; font-size: 13px; font-weight: 600;
      background: #1c1c1c; color: #fff;
    }
    #${'nveda-gift-prompt'} button:disabled { opacity: .5; cursor: default; }
    .nveda-gift-prompt__text strong {
      font-weight: 600; font-size: 13px; letter-spacing: .01em;
      display: inline-block;
      animation: nveda-gift-pulse 1.6s ease-in-out infinite;
    }
    @keyframes nveda-gift-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: .45; }
    }
    @media (prefers-reduced-motion: reduce) {
      .nveda-gift-prompt__text strong { animation: none; }
    }
    .nveda-gift-prompt__text {
      font-size: 13px; line-height: 1.4; opacity: .95;
    }
    .nveda-goal { padding: 10px 0 2px; }
    .nveda-goal__msg {
      font-size: 13px; line-height: 1.35; margin: 0 0 7px;
      letter-spacing: .01em;
    }
    .nveda-goal__msg b { font-weight: 600; }
    .nveda-goal__track {
      position: relative; height: 5px; border-radius: 3px;
      background: rgba(0,0,0,.10); overflow: hidden;
    }
    .nveda-goal__fill {
      position: absolute; inset: 0 auto 0 0; width: 0;
      border-radius: 3px; transition: width .35s ease;
    }
    .nveda-goal--done .nveda-goal__msg { font-weight: 600; }

    .nveda-gift-banner {
      display: none; margin: 0 0 14px; padding: 10px 14px;
      border: 1px solid rgba(180,120,0,.35); border-radius: 6px;
      background: rgba(255,193,7,.12); color: #6b4c00;
      font-size: 13px; line-height: 1.4;
    }
  `;

  (function injectCss() {
    if (document.getElementById('nveda-gift-prompt-css')) return;
    const st = document.createElement('style');
    st.id = 'nveda-gift-prompt-css';
    st.textContent = PROMPT_CSS;
    (document.head || document.documentElement).appendChild(st);
  })();

  /* ---------- CONFIG — EDIT THESE ---------- */

  const CONFIG = {
    // Threshold in PAISE. ₹999 = 99900
    THRESHOLD: 99900,

    // Gift: Nveda Calcium, Magnesium, Zinc, Vit D3, K2 and B12 - 60 Tablets ( Free Gift )
    // SKU 8906100500124_1 | handle nveda-calcium-magnesium-zinc-vit-d3-k2-and-b12-tablets-60-tablets-free
    GIFT_VARIANT_ID: 54729556132119,

    // Policy = ALWAYS GIVE. Gift goes on every cart >= threshold,
    // even if the customer already has Ashwagandha.
    FALLBACK_VARIANT_ID: null,
    ASHWAGANDHA_HANDLES: [],

    // Line item property marker. Underscore = hidden from customer.
    GIFT_FLAG: '_free_gift',

    // Shown in the opt-back-in prompt. Keep it factual, no health claims.
    GIFT_LABEL: 'Nveda Calcium, Magnesium, Zinc, Vit D3, K2 and B12 - 60 Tablets',
    PROMPT_TEXT: 'Free gift available',
    PROMPT_CTA: 'Add to cart',

    // Progress bar in the drawer header.
    // {amount} is replaced with the shortfall, e.g. "₹40".
    SHOW_PROGRESS: true,
    PROGRESS_AWAY: 'You are {amount} away from a free gift',
    PROGRESS_DONE: 'Free gift added at no cost',
    PROGRESS_COLOR: '#1f4d2e',
    CURRENCY: '₹',


    // Theme: "Hubako Media <>Gokwik_4Dec" v5.1.0 (custom).
    //
    // RELOAD MODE (null). The theme runs its own drawer render that we
    // cannot hook, so partial re-rendering raced it and showed stale
    // lines. A reload is always correct.
    //
    // To try partial rendering again: DRAWER_SECTIONS: ['cart-drawer']
    DRAWER_SECTIONS: null
  };

  /* ---------- STATE ---------- */

  let running = false;
  const DISMISS_KEY = 'nveda_gift_dismissed';

  // Identity of the gift line as of the last sync. Lets us detect a
  // manual removal synchronously instead of racing an async getCart().
  let giftKey = null;   // cart line key
  let giftLine = null;  // 1-based line index

  const allGiftIds = () =>
    [CONFIG.GIFT_VARIANT_ID, CONFIG.FALLBACK_VARIANT_ID].filter(Boolean);

  /* ---------- HELPERS ---------- */

  const getCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json());

  const isGiftLine = (item) =>
    (item.properties && item.properties[CONFIG.GIFT_FLAG] === 'yes') ||
    allGiftIds().includes(item.variant_id);

  // Subtotal EXCLUDING gift lines, so a priced gift can't inflate the total
  const qualifyingSubtotal = (cart) =>
    cart.items.reduce(
      (sum, item) => (isGiftLine(item) ? sum : sum + item.final_line_price),
      0
    );

  const hasAshwagandha = (cart) =>
    cart.items.some(
      (item) =>
        !isGiftLine(item) &&
        CONFIG.ASHWAGANDHA_HANDLES.includes(item.handle)
    );

  const currentGift = (cart) => cart.items.find(isGiftLine) || null;
  const giftLinesOf = (cart) => cart.items.filter(isGiftLine);

  const dismissed = () => sessionStorage.getItem(DISMISS_KEY) === '1';

  /* ---------- CART MUTATIONS ---------- */

  const FAIL_KEY = 'nveda_gift_failed';

  // Collapses duplicate gift lines down to exactly one, qty 1. Self-heals
  // carts that already picked up a stray extra line from the race
  // addGift() guards against below, and is called on every sync() pass
  // (not just inside addGift) so an existing duplicate gets fixed the
  // next time the drawer opens, without needing a fresh add.
  async function dedupeGiftLines(cart) {
    const lines = giftLinesOf(cart);
    if (lines.length === 0) return null;

    if (lines.length > 1) {
      // This IS the exception case: something added the gift twice.
      // Log it, tell the customer we fixed it, then collapse to one.
      logGiftAnomaly('duplicate-lines-collapsed', { count: lines.length });
      showBanner('Free gift was already in your cart — removed the extra.');
      for (let i = 1; i < lines.length; i++) {
        await removeLine(lines[i].key);
      }
    }

    if (lines[0].quantity !== 1) {
      if (lines[0].quantity > 1) {
        logGiftAnomaly('duplicate-quantity-clamped', { quantity: lines[0].quantity });
      }
      await setLineQty(lines[0].key, 1);
    }
    return lines[0];
  }

  // Mutex: every caller (sync(), the opt-back-in button, concurrent
  // triggers firing within the same tick) shares ONE in-flight add
  // instead of each racing its own getCart() -> add.js round trip. That
  // exact race is what produced order #NVEDA340685: two overlapping
  // calls each read "no gift yet" before either write landed, so each
  // created its own line item — the cart ended up with two separate
  // qty-1 lines instead of one qty-2 (Shopify's /cart/add.js does not
  // always merge identical concurrent adds into a single line). The
  // previous version only clamped quantity on the FIRST matching line,
  // so a second, separate line went undetected.
  let addLock = null;

  function addGift(variantId) {
    if (addLock) return addLock;
    addLock = (async () => {
      try {
        const cart = await getCart();
        const existing = await dedupeGiftLines(cart);
        if (existing) return true;

        const res = await rawAdd(variantId);
        if (res && res.ok) {
          // Post-add safety net: collapse any stray duplicate lines and
          // clamp to exactly one unit.
          const after = await getCart();
          await dedupeGiftLines(after);
          return true;
        }
        sessionStorage.setItem(FAIL_KEY, '1');
        return false;
      } catch (_) {
        sessionStorage.setItem(FAIL_KEY, '1');
        return false;
      } finally {
        addLock = null;
      }
    })();
    return addLock;
  }

  function rawAdd(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { [CONFIG.GIFT_FLAG]: 'yes' }
          }
        ]
      })
    });
  }

  // Absolute set of a line quantity by its cart line key.
  function setLineQty(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
  }

  function removeLine(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  /* ---------- CORE ---------- */

  async function sync() {
    if (running) return;
    running = true;

    try {
      const cart = await getCart();
      const subtotal = qualifyingSubtotal(cart);
      const gift = await dedupeGiftLines(cart);

      giftKey = gift ? gift.key : null;
      giftLine = gift ? cart.items.indexOf(gift) + 1 : null;
      const qualifies = subtotal >= CONFIG.THRESHOLD;

      // Which gift SHOULD be in the cart right now
      let target = null;
      if (qualifies) {
        target = hasAshwagandha(cart)
          ? CONFIG.FALLBACK_VARIANT_ID // null under Exclude policy
          : CONFIG.GIFT_VARIANT_ID;
      }

      renderProgress(cart);

      // Declined but still eligible -> offer it back
      renderPrompt(qualifies && !gift && dismissed());

      // No change needed
      if (!target && !gift) return;
      if (gift && target && gift.variant_id === target) {
        // Clamp to a single unit if anything added extras.
        if (gift.quantity !== 1) {
          await setLineQty(gift.key, 1);
          await refresh();
          return;
        }
        lockQuantity();
        return;
      }

      // Below threshold, or gift no longer valid → remove
      if (gift) {
        await removeLine(gift.key);
        if (!target) {
          await refresh();
          return;
        }
      }

      // Customer manually removed it this session → don't fight them
      if (!gift && dismissed()) return;

      // A previous add failed (out of stock / unpublished). Stop trying,
      // otherwise reload mode would loop forever.
      if (sessionStorage.getItem(FAIL_KEY) === '1') return;

      if (target) {
        const added = await addGift(target);
        if (added) await refresh();
      }
    } catch (e) {
      // Fail silent — never block checkout over a freebie
      console.warn('[nveda-gift]', e);
    } finally {
      running = false;
    }
  }

  /* ---------- UI ---------- */

  // Cache-busted section fetch. Without this the browser can serve a
  // stale copy of ?sections=cart-drawer and the gift line goes missing.
  async function fetchSections() {
    try {
      const url =
        `${window.location.pathname}?sections=` +
        `${CONFIG.DRAWER_SECTIONS.join(',')}&_nv=${Date.now()}`;
      const res = await fetch(url, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      });
      return await res.json();
    } catch (_) {
      return null;
    }
  }

  async function refresh() {
    if (!CONFIG.DRAWER_SECTIONS) {
      window.location.reload();
      return;
    }
    try {
      const sections = await fetchSections();
      if (!sections) return;

      Object.keys(sections).forEach((id) => {
        const target = document.getElementById(`shopify-section-${id}`);
        if (!target) return;

        // Response INCLUDES the shopify-section wrapper, so replace
        // outerHTML. Using innerHTML would nest a duplicate wrapper.
        const doc = new DOMParser().parseFromString(sections[id], 'text/html');
        const fresh = doc.getElementById(`shopify-section-${id}`);
        if (fresh) {
          target.replaceWith(fresh);
        } else {
          target.innerHTML = sections[id];
        }
      });

      // Theme re-binds its own listeners on the new nodes; give it a tick
      document.dispatchEvent(new CustomEvent('cart:refreshed'));
      lockQuantity();
      syncCountBubbles();
      getCart().then((c) => {
        renderPrompt(
          qualifyingSubtotal(c) >= CONFIG.THRESHOLD &&
          !currentGift(c) &&
          dismissed()
        );
      }).catch(() => {});

      // The theme runs its own drawer render in parallel and can land
      // AFTER ours, repainting stale HTML. Re-check twice and repaint
      // if the DOM disagrees with the server cart.
      reconcile(700);
      reconcile(1600);
    } catch (e) {
      window.location.reload();
    }
  }

  // Shown when the customer declined the gift but still qualifies,
  // so they can opt back in instead of losing it for the session.
  function renderPrompt(show) {
    const existing = document.getElementById(PROMPT_ID);
    if (!show) {
      if (existing) existing.remove();
      return;
    }
    if (existing) return;

    const host =
      document.querySelector('.m-cart-drawer__items') ||
      document.querySelector('[data-minimog-cart-items]');
    if (!host) return;

    const box = document.createElement('div');
    box.id = PROMPT_ID;
    const esc = (t) =>
      String(t).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;',
        '"': '&quot;', "'": '&#39;'
      }[c]));

    box.innerHTML =
      '<span class="nveda-gift-prompt__text">' +
        '<strong>' + esc(CONFIG.PROMPT_TEXT) + '</strong><br>' +
        esc(CONFIG.GIFT_LABEL) +
      '</span>' +
      '<button type="button">' + esc(CONFIG.PROMPT_CTA) + '</button>';

    box.querySelector('button').addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      btn.disabled = true;
      btn.textContent = 'Adding...';
      sessionStorage.removeItem(DISMISS_KEY);
      sessionStorage.removeItem(FAIL_KEY);
      const added = await addGift(CONFIG.GIFT_VARIANT_ID);
      if (added) {
        await refresh();
      } else {
        btn.disabled = true;
        btn.textContent = 'Unavailable';
      }
    });

    host.prepend(box);
  }

  const money = (paise) =>
    CONFIG.CURRENCY + Math.ceil(paise / 100).toLocaleString('en-IN');

  // Threshold progress bar. Mounts into the theme's own cart-goal slot.
  function renderProgress(cart) {
    if (!CONFIG.SHOW_PROGRESS) return;

    const mount =
      document.querySelector('foxkit-cart-goal') ||
      document.querySelector('.m-cart-drawer__header');
    if (!mount) return;

    const subtotal = qualifyingSubtotal(cart);
    const hasGift = !!currentGift(cart);

    // Nothing to show on an empty cart
    if (!cart.items.length) {
      mount.innerHTML = '';
      return;
    }

    const pct = Math.min(100, Math.round((subtotal / CONFIG.THRESHOLD) * 100));
    const short = Math.max(0, CONFIG.THRESHOLD - subtotal);

    const msg = hasGift
      ? CONFIG.PROGRESS_DONE
      : CONFIG.PROGRESS_AWAY.replace('{amount}', '<b>' + money(short) + '</b>');

    mount.innerHTML =
      '<div class="nveda-goal' + (hasGift ? ' nveda-goal--done' : '') + '">' +
        '<p class="nveda-goal__msg">' + msg + '</p>' +
        '<div class="nveda-goal__track">' +
          '<div class="nveda-goal__fill" style="width:' + (hasGift ? 100 : pct) +
          '%;background:' + CONFIG.PROGRESS_COLOR + '"></div>' +
        '</div>' +
      '</div>';
  }

  // Compares what the drawer is showing against the real cart.
  // Repaints only when they disagree, so it is cheap and idempotent.
  function reconcile(delay) {
    setTimeout(() => {
      getCart().then((c) => {
        const cartHasGift = !!currentGift(c);
        const domHasGift = !!document.querySelector('[data-nveda-gift-line]');
        if (cartHasGift === domHasGift) {
          lockQuantity();
          syncCountBubbles();
          return;
        }
        repaint();
      }).catch(() => {});
    }, delay);
  }

  // Section re-render WITHOUT triggering another reconcile loop.
  async function repaint() {
    if (!CONFIG.DRAWER_SECTIONS) return;
    try {
      const sections = await fetchSections();
      if (!sections) return;

      Object.keys(sections).forEach((id) => {
        const target = document.getElementById(`shopify-section-${id}`);
        if (!target) return;
        const doc = new DOMParser().parseFromString(sections[id], 'text/html');
        const fresh = doc.getElementById(`shopify-section-${id}`);
        if (fresh) target.replaceWith(fresh);
      });

      document.dispatchEvent(new CustomEvent('cart:refreshed'));
      lockQuantity();
      syncCountBubbles();
    } catch (_) {}
  }

  // Minimog's count bubbles can go stale after a section replace.
  // Re-read the live cart and write the true item_count.
  function syncCountBubbles() {
    getCart().then((c) => {
      document
        .querySelectorAll('.m-cart-count, .m-cart-count-bubble')
        .forEach((el) => { el.textContent = c.item_count; });
    }).catch(() => {});
  }

  // Hide +/- and remove control on the gift line.
  // Minimog markup: m-quantity-input.m-quantity + .m-cart-item__remove
  // CSS in cart-drawer-item.liquid does this on page load; this is the
  // fallback for nodes injected after a section re-render.
  function lockQuantity() {
    document.querySelectorAll('[data-nveda-gift-line]').forEach((el) => {
      // Quantity is locked, but REMOVE stays available on purpose:
      // customers must be able to decline the gift.
      el.querySelectorAll(
        '.m-quantity, m-quantity-input'
      ).forEach((n) => { n.style.display = 'none'; });
    });
  }

  /* ---------- TRIGGERS ---------- */

  const CART_RE = /\/cart\/(add|change|update|clear)/;

  function noteManualRemoval(bodyStr) {
    let body;
    try {
      body = JSON.parse(bodyStr || '{}');
    } catch (_) {
      // Minimog may post form-encoded; fall back to the async check below
      body = null;
    }

    if (body && body.quantity === 0) {
      // Synchronous match against the last known gift line — set the flag
      // BEFORE sync() runs, so the gift is never re-added under the customer.
      const hitByKey = giftKey && body.id === giftKey;
      const hitByLine = giftLine && Number(body.line) === giftLine;
      if (hitByKey || hitByLine) {
        sessionStorage.setItem(DISMISS_KEY, '1');
        giftKey = null;
        giftLine = null;
        return;
      }
    }

    // Fallback: gift gone while still qualifying means it was removed
    getCart().then((c) => {
      if (!currentGift(c) && qualifyingSubtotal(c) >= CONFIG.THRESHOLD) {
        sessionStorage.setItem(DISMISS_KEY, '1');
      }
    }).catch(() => {});
  }

  // --- fetch ---
  const origFetch = window.fetch;
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    return origFetch.apply(this, args).then((res) => {
      if (CART_RE.test(url)) {
        if (/\/cart\/change/.test(url)) noteManualRemoval(args[1]?.body);
        setTimeout(sync, 400);
      }
      return res;
    });
  };

  // --- XMLHttpRequest (Minimog cart.js uses this) ---
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__nvUrl = url;
    return origOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (this.__nvUrl && CART_RE.test(this.__nvUrl)) {
      this.addEventListener('load', () => {
        if (/\/cart\/change/.test(this.__nvUrl) && typeof body === 'string') {
          noteManualRemoval(body);
        }
        setTimeout(sync, 400);
      });
    }
    return origSend.call(this, body);
  };

  // --- theme / app events ---
  // Redraw the bar whenever the drawer is opened
  document.addEventListener('click', () => {
    setTimeout(() => {
      const d = document.getElementById('MinimogCartDrawer');
      if (d && d.classList.contains('m-cart-drawer--active')) {
        getCart().then(renderProgress).catch(() => {});
      }
    }, 250);
  }, true);

  ['cart:updated', 'cart:refresh', 'cart:change', 'CartDrawer:open',
   'cart-drawer:open', 'ajaxCart.afterCartLoad'].forEach((evt) => {
    document.addEventListener(evt, () => setTimeout(sync, 400));
  });

  // --- primary trigger: watch Minimog's cart count bubble ---
  // Zero extra requests, and immune to other apps overwriting fetch/XHR.
  let lastCount = null;
  function watchCartCount() {
    const obs = new MutationObserver(() => {
      const el = document.querySelector('.m-cart-count');
      if (!el) return;
      const n = el.textContent.trim();
      if (n !== lastCount) {
        lastCount = n;
        setTimeout(sync, 400);
      }
    });
    obs.observe(document.body, {
      childList: true, subtree: true, characterData: true
    });
    const el = document.querySelector('.m-cart-count');
    if (el) lastCount = el.textContent.trim();
  }

  if (document.body) {
    watchCartCount();
  } else {
    document.addEventListener('DOMContentLoaded', watchCartCount);
  }

  // --- backup poll while the drawer is open ---
  let lastSig = '';
  setInterval(() => {
    if (document.hidden) return;
    const drawer = document.getElementById('MinimogCartDrawer');
    if (!drawer || !drawer.classList.contains('m-cart-drawer--active')) return;

    getCart().then((c) => {
      const sig = c.item_count + ':' + c.total_price;
      if (sig !== lastSig) {
        lastSig = sig;
        sync();
      }
    }).catch(() => {});
  }, 2500);

  // Script may load after DOMContentLoaded has already fired
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();